import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  HTMLElementWithAnchor,
  Popover2025,
  PopoverPlacement,
} from './Popover2025';
import { StyledPopoverContainer } from './StyledPopoverContainer';
import { POPOVER_DELAY } from './consts';

export const BULK_POPOVER_ATTR = 'data-bulk-popover';

const DISCONNECT_POLL_MS = 250;

const DEFAULT_PLACEMENT: PopoverPlacement = { side: 'top', overflow: 'center' };

type ActivePopover = {
  content: ReactNode;
  key: number;
};

type DelegatedHover = {
  active: ActivePopover | null;
  anchorRef: RefObject<HTMLElementWithAnchor | null>;
  close: VoidFunction;
  /** Cursor entered the popover — cancel a pending close so it stays open. */
  keepOpen: VoidFunction;
  /** Cursor left the popover — start the close delay. */
  scheduleClose: VoidFunction;
};

const useDelegatedHover = (
  rootRef: RefObject<HTMLElement | null>,
  resolveContent: (trigger: HTMLElement | SVGElement) => ReactNode | null
): DelegatedHover => {
  const anchorRef = useRef<HTMLElementWithAnchor | null>(null);
  const keyRef = useRef(0);
  const [active, setActive] = useState<ActivePopover | null>(null);

  // Held in a ref so the delegated-listener effect never re-runs when a
  // consumer passes an inline `resolveContent`. Re-running would remove and
  // re-add the document listeners and cancel any pending hover timer — a
  // consumer re-render inside the open delay would then drop the popover.
  const resolveContentRef = useRef(resolveContent);
  useEffect(() => {
    resolveContentRef.current = resolveContent;
  }, [resolveContent]);

  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingOpen = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);
  const cancelPendingClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  // Deliberately does NOT cancel a pending open: a scheduled close is for the
  // current anchor, and the pending open may belong to a different trigger the
  // cursor has moved on to.
  const close = useCallback(() => {
    cancelPendingClose();
    anchorRef.current = null;
    setActive(null);
  }, [cancelPendingClose]);

  const scheduleClose = useCallback(() => {
    cancelPendingClose();
    closeTimer.current = setTimeout(close, POPOVER_DELAY);
  }, [cancelPendingClose, close]);

  useEffect(() => {
    // Listen on `document` and re-read `rootRef` per event rather than binding
    // to `rootRef.current` once: the container's subtree (or the container
    // element itself) can be rebuilt — e.g. a graph re-layout — and a captured
    // reference would go stale.
    const triggerWithinRoot = (
      event: MouseEvent
    ): HTMLElement | SVGElement | null => {
      if (!(event.target instanceof Element)) return null;
      const trigger = event.target.closest<HTMLElement | SVGElement>(
        `[${BULK_POPOVER_ATTR}]`
      );
      const root = rootRef.current;
      return trigger && root?.contains(trigger) ? trigger : null;
    };

    const handleOver = (event: MouseEvent) => {
      const trigger = triggerWithinRoot(event);
      if (!trigger) return;
      if (trigger === anchorRef.current) {
        cancelPendingClose();
        return;
      }
      cancelPendingOpen();
      openTimer.current = setTimeout(() => {
        if (!trigger.isConnected) return;
        const content = resolveContentRef.current(trigger);
        if (content === null || content === undefined) {
          // Moved onto a trigger with nothing to show — close whatever was open.
          close();
          return;
        }
        anchorRef.current = trigger as HTMLElementWithAnchor;
        keyRef.current += 1;
        setActive({ content, key: keyRef.current });
      }, POPOVER_DELAY);
    };

    const handleOut = (event: MouseEvent) => {
      const trigger = triggerWithinRoot(event);
      if (!trigger) return;
      const movingTo = event.relatedTarget as Element | null;
      // Ignore moves that stay within the trigger's own descendants.
      if (movingTo && trigger.contains(movingTo)) return;
      // Moving straight to another trigger: let that trigger's mouseover swap
      // the popover in, rather than closing here and racing its open.
      const nextTrigger = movingTo?.closest<HTMLElement | SVGElement>(
        `[${BULK_POPOVER_ATTR}]`
      );
      if (nextTrigger && rootRef.current?.contains(nextTrigger)) return;
      if (trigger === anchorRef.current) {
        scheduleClose();
      } else {
        cancelPendingOpen();
      }
    };

    document.addEventListener('mouseover', handleOver, true);
    document.addEventListener('mouseout', handleOut, true);
    return () => {
      document.removeEventListener('mouseover', handleOver, true);
      document.removeEventListener('mouseout', handleOut, true);
      cancelPendingOpen();
      cancelPendingClose();
    };
  }, [rootRef, cancelPendingOpen, cancelPendingClose, scheduleClose, close]);

  return {
    active,
    anchorRef,
    close,
    keepOpen: cancelPendingClose,
    scheduleClose,
  };
};

const useCloseOnAnchorDisconnect = (
  anchorRef: RefObject<HTMLElementWithAnchor | null>,
  isOpen: boolean,
  onDisconnect: VoidFunction
) => {
  useEffect(() => {
    if (!isOpen) return;
    // A low-frequency `isConnected` poll rather than a MutationObserver: the
    // anchor is often disconnected by an ancestor being rebuilt (a graph
    // re-layout swapping the container subtree), which a childList observer on
    // the anchor's own parent would miss. `isConnected` catches every case, and
    // the poll only runs while a popover is open.
    const intervalId = setInterval(() => {
      if (anchorRef.current && !anchorRef.current.isConnected) {
        onDisconnect();
      }
    }, DISCONNECT_POLL_MS);
    return () => clearInterval(intervalId);
  }, [anchorRef, isOpen, onDisconnect]);
};

export type BulkHoverPopoverProps = {
  resolveContent: (trigger: HTMLElement | SVGElement) => ReactNode | null;
  /**
   * Scopes the instance: only `[data-bulk-popover]` triggers inside this
   * container are served, which also isolates it from other `BulkHoverPopover`s.
   * Read live on every event, so it survives the container's subtree — or the
   * container element itself — being rebuilt (e.g. a graph re-layout). Keep it
   * pointed at a stable wrapper that the triggers render within.
   */
  rootRef: RefObject<HTMLElement | null>;
  placement?: PopoverPlacement;
  acceptsMouse?: boolean;
};

/**
 * Delegated hover popover for view surfaces with many DOM labels
 * (componentMatrix cards, dependency-map nodes, roadmap rows, graph labels) —
 * where wrapping each trigger with `WithUncontrolledPopover` is impractical
 * because there are hundreds of them. Triggers can be HTML or SVG elements.
 *
 * NOT the default. For ordinary React triggers — buttons, menu items, a handful
 * of labels — use `WithUncontrolledPopover` (wrap the child): simpler,
 * co-located, type-safe, easier to make accessible.
 *
 * Render ONE `BulkHoverPopover` per view, pass its container as `rootRef`, and
 * stamp each trigger with `data-bulk-popover`. A single delegated listener
 * resolves the hovered trigger within `rootRef` and shows one popover2025
 * popover anchored to it.
 */
export const BulkHoverPopover = ({
  resolveContent,
  rootRef,
  placement = DEFAULT_PLACEMENT,
  acceptsMouse = true,
}: BulkHoverPopoverProps) => {
  const { active, anchorRef, close, keepOpen, scheduleClose } =
    useDelegatedHover(rootRef, resolveContent);
  useCloseOnAnchorDisconnect(anchorRef, active !== null, close);

  const instanceId = `bulk-popover-${active?.key ?? 'closed'}`;

  return (
    <Popover2025
      key={instanceId}
      id={instanceId}
      isOpen={active !== null}
      onClose={close}
      anchorRef={anchorRef}
      placement={placement}
      role="tooltip"
    >
      <StyledPopoverContainer
        isOpen={active !== null}
        preferredSide={placement.side}
        anchorId={instanceId}
        anchorRef={anchorRef}
        onMouseEnter={acceptsMouse ? keepOpen : undefined}
        onMouseLeave={acceptsMouse ? scheduleClose : undefined}
        disablePointerEvents={!acceptsMouse}
      >
        {active?.content}
      </StyledPopoverContainer>
    </Popover2025>
  );
};
